import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncStatus, Transaction } from '@algoan/dashboard/model';
import { updateAuthInformation } from './auth-slice';

export type TransactionErrorMessages = 'noTransactionsError' | 'genericError';

export interface TransactionReducerState {
  transactions: Transaction[];
  transactionsFetchingStatus: AsyncStatus;
  transactionsError: TransactionErrorMessages | undefined;
}

const getInitialState = (): TransactionReducerState => ({
  transactions: [],
  transactionsFetchingStatus: AsyncStatus.IDLE,
  transactionsError: undefined,
});

const initialState = getInitialState();

const transactionSlice = createSlice({
  name: 'transaction',
  initialState,
  reducers: {
    fetchTransactionsStart(state) {
      state.transactionsFetchingStatus = AsyncStatus.PENDING;
      state.transactionsError = undefined;
    },
    fetchTransactionsSuccess(state, action: PayloadAction<Transaction[]>) {
      state.transactions = action.payload;
      state.transactionsFetchingStatus = AsyncStatus.RESOLVED;
      state.transactionsError = undefined;
    },
    fetchTransactionsFailure(state, action: PayloadAction<{ error: TransactionErrorMessages }>) {
      state.transactionsFetchingStatus = AsyncStatus.REJECTED;
      state.transactionsError = action.payload.error;
    },
    updateTransactionComment(state, action: PayloadAction<{ id: string; comment?: string }>) {
      const transaction = state.transactions?.find((transaction) => transaction.id === action.payload.id);

      if (transaction !== undefined) {
        transaction.comment = action.payload.comment;
      }
    },
    resetTransactionState: () => getInitialState(),
  },
  extraReducers: (builder) => {
    builder.addCase(updateAuthInformation, (state, action) => {
      if (typeof action.payload === 'undefined') {
        // we clear the state on logout
        return getInitialState();
      }

      return state;
    });
  },
});

export const { reducer: transactionReducer } = transactionSlice;
export const {
  fetchTransactionsFailure,
  fetchTransactionsStart,
  fetchTransactionsSuccess,
  resetTransactionState,
  updateTransactionComment,
} = transactionSlice.actions;
